import {
    useCallback, useEffect, useMemo, useRef, useState, ChangeEvent,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    date, number, object, string,
} from 'yup';

import ServiceManager from 'services/ServiceManager';
import { Modal } from 'ui-library';

import { TARGET_FIELDS } from 'components/MailingAddress/MailingAddress';
import { uploadDocumentsPageLink } from 'pages/ClientOnboardingFlow/helpers/links';
import { useContact } from 'domain/ClientService/hooks/useContact';
import { useEditCustomer } from 'domain/ClientService';
import { OPTION_LIST, switzLandCountryId } from 'constants/constants';
import { useOptionList } from 'hooks/useOptionList';

import {
    IsAllDataNull,
    validateDateOfBirth,
    validateEmailAdress,
    validateInputLength,
    validateInputNumber,
    validateInputString,
    validatePhoneNumber,
    validateRadio,
} from 'validation/validation';

import { useQueryClient } from '@tanstack/react-query';
import { FIELD_LENGTH } from '../../../../ClientOverview/constants';
import { setUserRegisterId } from '../../../helpers/userRegisterId';
import { autocomplete } from './helpers/autocomplete';
import { convertDate, getOldness } from './helpers/convertDate';
import { errorModel } from './models/errorModel';
import { getModel, PersonalData } from './models/getModel';
import { sendModel } from './models/sendModel';
import { getModelByZKP } from './models/zkpClientModel';
import { getUserId } from './services/getUserId';
import postCodeSwitzland from './constants/postCodes.json';

// Define the schema type using Yup's InferType
const zkpUserSchema = object({
    birthDate: date().nullable(),
    city: string().nullable(),
    civilStatus: number().nullable(),
    countryOfResidence: number().integer().nullable(),
    email: string().email().nullable(),
    firstName: string().nullable(),
    lastName: string().nullable(),
    gender: number().integer().nullable(),
    language: number().integer().nullable(),
    mobileNumber: string().nullable(),
    postCode: string().nullable(),
    street: string().nullable(),
    streetNumber: string().nullable(),
});

interface LocationState {
    externalClientId?: string;
}

const useNewPersonalInformation = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const state = location.state as LocationState;

    const queryClient = useQueryClient();

    const mounted = useRef<boolean>(false);

    const contactExternalId = useMemo(
        () => state?.externalClientId,
        [state?.externalClientId],
    );

    const userRegisterId = getUserId();
    const { getContact } = useContact({ contactId: userRegisterId as string });

    const {
        lists: [
            optionGender,
            optionMaritalStatus,
            optionLanguage,
            optionCountry,
            optionContactType,
            optionContactStatus,
        ],
    } = useOptionList([
        OPTION_LIST.gender,
        OPTION_LIST.maritalStatus,
        OPTION_LIST.language,
        OPTION_LIST.country,
        OPTION_LIST.contactType,
        OPTION_LIST.contactStatus,
    ]);

    const contactType = useMemo(
        () => optionContactType?.find((type: any) => type?.key === '1')?.id,
        [optionContactType],
    );
    const contactStatus = useMemo(
        () => optionContactStatus?.find((type: any) => type?.key === '1')?.id,
        [optionContactStatus],
    );

    const { createContact } = useEditCustomer();

    const [isSubmitTry, setSubmitTry] = useState<boolean>(false);
    const [isJunior, setJunior] = useState<boolean>(false);
    const [data, setData] = useState<PersonalData>({} as PersonalData);
    const [cachedData, setCachedData] = useState<PersonalData>({} as PersonalData);

    const [error, setError] = useState<{ message: string } | null>(null);
    const [errorData, setErrorData] = useState<Record<string, any>>({});
    const [errorPage, setErrorPage] = useState<string | undefined>();

    const [isLoading, setLoading] = useState<boolean>(true);
    const [loadingUserData, setLoadingUserData] = useState<boolean>(false);
    const [isLoadingAPICall, setLoadingAPICall] = useState<boolean>(false);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (contactExternalId) {
            (async () => {
                setLoadingAPICall(true);
                try {
                    const response = await ServiceManager.customClientService(
                        'getContactDetails',
                        [{ contactExternalId }],
                    ) as any;
                    const validatedData = await zkpUserSchema.validate(response.data);
                    const adaptedData = getModelByZKP(validatedData);

                    const newData: PersonalData = { ...data, ...adaptedData };

                    setData(newData);
                    setCachedData(newData);
                } catch (err: any) {
                    if (state) delete state.externalClientId;
                    setErrorPage(t('onboardingFlow.personalPage.zkp.error.clientDetailsNotAvailable'));
                } finally {
                    setLoadingAPICall(false);
                }
            })();
        }
    }, [contactExternalId]);

    const goToAction = async () => {
        setSubmitTry(true);
        setLoadingAPICall(true);
        try {
            const errors:any = await errorModel(data, userRegisterId, isJunior);

            setErrorData(errors);
            setErrorPage(
                t(errors.firstLastLengthCombined, {
                    max: FIELD_LENGTH.firstAndLastNameCombined,
                }),
            );

            if (IsAllDataNull(errors) === false) return;

            const details = sendModel({
                ...data,
                contactType,
                contactStatus,
            });

            if (userRegisterId) {
                await ServiceManager.clientService('patchContact', [
                    userRegisterId,
                    { details },
                ]);
                navigate(uploadDocumentsPageLink);
            } else {
                const memberId = localStorage.getItem('memberId');
                const dataUser:any = await createContact({
                    data: {
                        contactTypeId: 1,
                        memberIds: [memberId],
                        contactExternalId,
                        details,
                    },
                });

                setUserRegisterId(dataUser?.id);
                navigate(uploadDocumentsPageLink);
                queryClient.invalidateQueries({ queryKey: ['getClients'] });
            }
        } catch (err: any) {
            const errorMessage = [
                err?.response?.data?.properties?.ValidationErrors?.[0]?.message,
                err?.error?.response?.data?.errorMessages?.[0],
            ].filter(Boolean);

            setErrorPage(errorMessage[0] || err.message);
        } finally {
            setLoadingAPICall(false);
        }
    };

    useEffect(() => {
        if (userRegisterId) {
            setLoadingUserData(true);
            getContact({ contactId: userRegisterId })
                .then((contactDataForm: any) => {
                    setData(getModel(contactDataForm));
                })
                .catch((err: Error) => {
                    setError({ message: err.message });
                })
                .finally(() => setLoadingUserData(false));
        }
    }, [userRegisterId]);

    useEffect(() => {
        if (
            optionCountry !== undefined
            && optionMaritalStatus !== undefined
            && optionLanguage !== undefined
        ) {
            setLoading(false);
        }
    }, [optionGender, optionMaritalStatus, optionLanguage, optionCountry]);

    // Switzerland Autocomplete Logic
    useEffect(() => {
        if (Number(data?.countryOfResidence) === switzLandCountryId) {
            const index = autocomplete(
                data.countryOfResidence,
                postCodeSwitzland,
                switzLandCountryId,
                data.PLZ,
            );

            if (index !== undefined && postCodeSwitzland[index]) {
                const ORT = postCodeSwitzland[index]?.Ortschaftsname;

                setErrorData((prev) => ({
                    ...prev,
                    PLZ: validateInputString(data.PLZ),
                    ORT: ORT && (validateInputString(ORT) || validateInputLength(ORT, FIELD_LENGTH.city)),
                }));
                setData((prev) => ({ ...prev, ORT, readOnly: true }));
            } else {
                setData((prev) => ({ ...prev, ORT: '', readOnly: true }));
            }
        } else {
            setData((prev) => ({ ...prev, readOnly: false }));
        }
    }, [data.PLZ, data.countryOfResidence]);

    const changeMobileMask = (value: string, key: string, symbol: string) => {
        setErrorData((prev) => ({ ...prev, [key]: validatePhoneNumber(value) }));
        if (value !== undefined) {
            const formattedValue = value.indexOf(symbol) === -1 ? symbol.concat(value) : value;

            setData((prev) => ({ ...prev, [key]: formattedValue }));
        }
    };

    const passData = (value: any, key: string) => {
        setErrorData((prev) => ({ ...prev, [key]: validateRadio(value) }));
        setData((prev) => ({ ...prev, [key]: value }));
    };

    const onDateClick = (chosenDate: Date | string) => {
        const formatDateValue = convertDate(chosenDate);

        setData((prev) => ({
            ...prev,
            dateOfBirth: `${formatDateValue}`,
            hideDatePickerValue: false,
        }));
        setErrorData((prev) => ({
            ...prev,
            dateOfBirth: validateDateOfBirth(formatDateValue),
        }));
    };

    const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e?.target?.value;

        setData((prev) => ({ ...prev, primaryEmail: val }));
        setErrorData((prev) => ({ ...prev, primaryEmail: validateEmailAdress(val) }));
    };

    useEffect(() => {
        if (data?.dateOfBirth) {
            const fullYears = getOldness(data.dateOfBirth);
            const isJuniorCase = fullYears < 18;

            setJunior(isJuniorCase);

            if (isSubmitTry) {
                setErrorData((prev) => ({
                    ...prev,
                    mailRecipientAddress: isJuniorCase
                        ? validateInputNumber(data.mailRecipientAddressCountry)
                        : null,
                }));
            }
        }
    }, [data?.dateOfBirth, isSubmitTry]);

    const handleSubmitMailingAddress = useCallback(
        async (mailingData: any, action: number | undefined) => {
            if (userRegisterId) {
                await ServiceManager.customClientService('patchMailingAddress', [
                    { contactId: userRegisterId, data: mailingData, action },
                ]);

                if (action === 1) {
                    Modal.success({
                        title: t('MailingAddress.successful.delete.title'),
                        content: t('MailingAddress.success.delete.content'),
                        okText: t('MailingAddress.success.okButton.label'),
                        onOk: () => { window.location.reload(); },
                    });
                } else {
                    window.location.reload();
                }
            } else if (action === 1) {
                const dataCopy = { ...data };

                TARGET_FIELDS.forEach((fieldName) => { delete dataCopy[fieldName]; });
                setData(dataCopy);
                setErrorData((prev) => ({
                    ...prev,
                    mailRecipientAddress: isJunior ? validateInputNumber(dataCopy.mailRecipientAddressCountry) : null,
                }));
                Modal.success({ title: t('MailingAddress.successful.delete.title') });
            } else {
                const mailRecipientFields = TARGET_FIELDS.reduce((accu: any, fieldName) => {
                    accu[fieldName] = mailingData?.[fieldName];

                    return accu;
                }, {});

                setData((prev) => ({ ...prev, ...mailRecipientFields }));
                setErrorData((prev) => ({
                    ...prev,
                    mailRecipientAddress: isJunior ? validateInputNumber(mailRecipientFields.mailRecipientAddressCountry) : null,
                }));
            }
        },
        [data, userRegisterId, isJunior, t],
    );

    return {
        isLoading,
        loadingUserData,
        error,
        data,
        optionGender,
        passData,
        errorData,
        cachedData,
        setData,
        setErrorData,
        onDateClick,
        optionCountry,
        optionMaritalStatus,
        optionLanguage,
        isJunior,
        handleSubmitMailingAddress,
        changeMobileMask,
        onChangeEmail,
        errorPage,
        isLoadingAPICall,
        goToAction,
    };
};

export default useNewPersonalInformation;
